import type { Ability, Creation, HitPower, TimedEffect } from "../src/domain/types";

export interface Line {
  style: "section" | "stat" | "power" | "ability";
  text: string;
}

const ROMAN = ["", "I", "II", "III", "IV", "V"];

export function describeCreation(c: Creation): Line[] {
  if (c.food) return foodLines(c);
  if (c.mob) return mobLines(c);
  return [...statLines(c), ...hitLines(c.hitPowers), ...abilityLines(c.ability, c.cooldownSeconds)];
}

export function titleCase(id: string): string {
  return id.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

function statLines(c: Creation): Line[] {
  const s = c.stats;
  const stats: string[] = [];
  if (s.attackDamage !== undefined) stats.push(`${s.attackDamage} Attack Damage`);
  if (s.attackSpeed !== undefined) stats.push(`${s.attackSpeed} Attack Speed`);
  if (s.armor !== undefined) stats.push(`+${s.armor} Armor`);
  if (s.armorToughness) stats.push(`+${s.armorToughness} Armor Toughness`);
  if (s.moveSpeed) stats.push(`${s.moveSpeed > 0 ? "+" : ""}${Math.round(s.moveSpeed * 100)}% Speed`);
  if (s.maxHealth) stats.push(`+${s.maxHealth / 2} Hearts`);
  if (s.durability) stats.push(`${s.durability} Durability`);
  if (!stats.length) return [];
  const where = c.kind === "armor" ? "When worn:" : "When in Main Hand:";
  return [{ style: "section", text: where }, ...stats.map((text) => ({ style: "stat" as const, text }))];
}

function hitLines(powers: HitPower[]): Line[] {
  if (!powers.length) return [];
  return [{ style: "section", text: "On hit:" }, ...powers.map((p) => ({ style: "power" as const, text: hitText(p) }))];
}

function hitText(p: HitPower): string {
  switch (p.type) {
    case "effect": return `${effectName(p)} for ${p.seconds}s on the target`;
    case "self_effect": return `You get ${effectName(p)} for ${p.seconds}s`;
    case "ignite": return `Sets the target on fire for ${p.seconds}s`;
    case "lightning": return "Lightning strikes the target";
    case "extra_damage": return `+${p.amount} magic damage`;
  }
}

function abilityLines(a: Ability | null, cooldown: number): Line[] {
  if (!a) return [];
  return [{ style: "section", text: `Right-click (every ${cooldown}s):` }, { style: "ability", text: abilityText(a) }];
}

function abilityText(a: Ability): string {
  switch (a.type) {
    case "buff": return a.effects.map((e) => `${effectName(e)} for ${e.seconds}s`).join(", ");
    case "heal": return `Heal (power ${a.power})`;
    case "leap": return `Super leap (power ${a.power})`;
    case "blink": return `Teleport ${a.distance} blocks forward`;
    case "lightning": return `Lightning strike ${a.distance} blocks ahead`;
    case "meteor": return `Meteor ${a.distance} blocks ahead (boom ${a.power})`;
    case "shockwave": return `Shockwave: ${a.damage} damage to mobs within ${a.radius} blocks`;
    case "summon_allies": return `Summon ${a.count} ${titleCase(a.mob)}${a.count > 1 ? "s" : ""}`;
  }
}

function foodLines(c: Creation): Line[] {
  const food = c.food!;
  return [
    { style: "section", text: "When eaten:" },
    { style: "stat", text: `Fills ${food.nutrition} hunger` },
    ...food.effects.map((e) => ({ style: "power" as const, text: `${effectName(e)} for ${e.seconds}s` })),
  ];
}

function mobLines(c: Creation): Line[] {
  const mob = c.mob!;
  const lines: Line[] = [
    { style: "section", text: `Spawn egg for a custom ${titleCase(mob.entity)}:` },
    { style: "stat", text: `${mob.health} health (${mob.health / 2} hearts)` },
  ];
  if (mob.scale !== 1) lines.push({ style: "stat", text: `Size x${mob.scale}` });
  lines.push({ style: "stat", text: `${mob.damage} attack damage` });
  if (mob.speedLevel) lines.push({ style: "stat", text: mob.speedLevel > 0 ? `Fast (speed ${mob.speedLevel})` : `Slow (${-mob.speedLevel})` });
  if (mob.mainhand) lines.push({ style: "stat", text: `Holds a ${titleCase(mob.mainhand)}` });
  if (mob.glowing) lines.push({ style: "power", text: "Glows through walls" });
  for (const e of mob.effects) lines.push({ style: "power", text: `Always has ${effectName(e)}` });
  return lines;
}

function effectName(e: Pick<TimedEffect, "effect" | "level">): string {
  return `${titleCase(e.effect)} ${ROMAN[e.level] ?? e.level}`;
}
